import { compose } from 'redux';
import { createSelector } from 'reselect';

import type {
  Costs,
  DailyCost,
  DriverNames,
  FuelCostByDay,
  FuelCosts,
  MaintenanceCosts,
  MaintenanceCostsByDay,
  UUID,
} from '@cobliteam/common-types';

import { mapWebData } from '@cobliteam/api-lib';

import type { CostsState } from '../costsStore';

const selectCostsDomain = (state: CostsState) => state.costs;

export const selectDriverNamesById = compose(
  (state) => state.driverNamesByIdWebData,
  selectCostsDomain
);

const selectFuelCosts = compose(
  (state) => state.fuelCostsWebData,
  selectCostsDomain
);

const selectMaintenanceCosts = compose(
  (state) => state.maintenanceCostsWebData,
  selectCostsDomain
);

export const selectCostsReport = compose(
  (state) => state.costsReportWebData,
  selectCostsDomain
);

const mapVehicleIdsWithAnyCost = (
  fuelCosts: FuelCosts,
  maintenanceCosts: MaintenanceCosts
) => {
  const fuelCostVehicleIds = Object.keys(fuelCosts);
  const maintenanceCostsVehicleIds = Object.keys(maintenanceCosts);
  return new Set([...fuelCostVehicleIds, ...maintenanceCostsVehicleIds]);
};

export const selectVehicleIdsWithAnyCost = createSelector(
  selectFuelCosts,
  selectMaintenanceCosts,
  mapWebData(mapVehicleIdsWithAnyCost)
);

const mapFuelCostByVehicleId = (fuelCosts: FuelCosts) => {
  const vehiclesTotalCost: Array<[string, number]> = Object.keys(fuelCosts).map(
    (vehicleId) => {
      const vehicleTotalFuelCost = fuelCosts[vehicleId].reduce(
        (totalCosts, dailyCosts) => totalCosts + (dailyCosts.cost || 0),
        0
      );
      return [vehicleId, vehicleTotalFuelCost];
    }
  );
  return new Map(vehiclesTotalCost);
};

export const selectFuelCostByVehicleId = createSelector(
  selectFuelCosts,
  mapWebData(mapFuelCostByVehicleId)
);

const mapMaintenanceCostByVehicleId = (maintenanceCosts: MaintenanceCosts) => {
  const vehiclesTotalCost: Array<[string, number]> = Object.keys(
    maintenanceCosts
  ).map((vehicleId) => {
    const vehicleTotalMaintenanceCost = maintenanceCosts[vehicleId].reduce(
      (totalCosts, dailyCosts) =>
        totalCosts +
        (dailyCosts.corrective || 0) +
        (dailyCosts.preventive || 0),
      0
    );
    return [vehicleId, vehicleTotalMaintenanceCost];
  });
  return new Map(vehiclesTotalCost);
};

export const selectMaintenanceCostByVehicleId = createSelector(
  selectMaintenanceCosts,
  mapWebData(mapMaintenanceCostByVehicleId)
);

const mapDrivenDistanceByVehicleId = (fuelCosts: FuelCosts) => {
  const vehiclesTotalDistance: Array<[string, number]> = Object.keys(
    fuelCosts
  ).map((vehicleId) => {
    const vehicleTotalDistance = fuelCosts[vehicleId].reduce(
      (totalDistance, dailyDistance) =>
        totalDistance + (dailyDistance.distanceDrivenInMeters || 0),
      0
    );
    return [vehicleId, vehicleTotalDistance];
  });
  return new Map(vehiclesTotalDistance);
};

const selectDrivenDistanceByVehicleId = createSelector(
  selectFuelCosts,
  mapWebData(mapDrivenDistanceByVehicleId)
);

export const getDayInfo = (
  fuelCost: FuelCostByDay,
  maintenanceCosts: MaintenanceCostsByDay,
  driversNamesById: DriverNames
): DailyCost => ({
  date: fuelCost.dateInMillis || maintenanceCosts.dateInMillis,
  distanceDrivenInMeters: fuelCost.distanceDrivenInMeters || 0,
  fuelCost: fuelCost.cost || 0,
  preventiveMaintenanceCost: maintenanceCosts.preventive || 0,
  correctiveMaintenanceCost: maintenanceCosts.corrective || 0,
  drivers: fuelCost.driversId
    ? fuelCost.driversId.map((id) => driversNamesById[id] || '')
    : [],
});

/**
 * It is expected that an vehicle has either fuel costs or maintenance costs otherwise it would have been
 * filtered out already. When a vehicle has costs for either of these types it will have an entry for each day
 * in between the chosen dates even when specific dates have no costs at all.
 */
const buildDailyCostsInfo = (
  dailyFuelCosts: FuelCostByDay[],
  dailyMaintenanceCosts: MaintenanceCostsByDay[],
  driversNamesById: DriverNames
): DailyCost[] => {
  const numOfDays = dailyFuelCosts.length || dailyMaintenanceCosts.length;
  return Array(numOfDays)
    .fill(null)
    .map((_, idx) => {
      const fuelCosts: FuelCostByDay = dailyFuelCosts[idx] || {};
      const maintenanceCosts: MaintenanceCostsByDay =
        dailyMaintenanceCosts[idx] || {};
      return getDayInfo(fuelCosts, maintenanceCosts, driversNamesById);
    });
};

const buildVehiclesCostsInfo = (
  vehicleIds: Set<UUID>,
  totalFuelCostsByVehicleId: Map<string, number>,
  totalMaintenanceCostsByVehicleId: Map<string, number>,
  totalDrivenDistanceById: Map<string, number>,
  fuelCosts: FuelCosts,
  maintenanceCosts: MaintenanceCosts,
  driversNamesById: DriverNames
): Costs[] => {
  return Array.from(vehicleIds).map((vehicleId) => {
    const vehicleFuelCosts = fuelCosts[vehicleId] || [];
    const vehicleMaintenanceCosts = maintenanceCosts[vehicleId] || [];
    const dailyCostInfo = buildDailyCostsInfo(
      vehicleFuelCosts,
      vehicleMaintenanceCosts,
      driversNamesById
    );

    return {
      vehicleId,
      costInfo: dailyCostInfo,
      fuelCost: totalFuelCostsByVehicleId.get(vehicleId) || 0,
      maintenanceCost: totalMaintenanceCostsByVehicleId.get(vehicleId) || 0,
      drivenDistanceInMeters: totalDrivenDistanceById.get(vehicleId) || 0,
    };
  });
};

export const selectVehiclesCostsInfo = createSelector(
  selectVehicleIdsWithAnyCost,
  selectFuelCostByVehicleId,
  selectMaintenanceCostByVehicleId,
  selectDrivenDistanceByVehicleId,
  selectFuelCosts,
  selectMaintenanceCosts,
  selectDriverNamesById,
  mapWebData(buildVehiclesCostsInfo)
);
